import { compareBib, dobForBib, type AgeGroupRegistrationInput } from './age-groups';
import { filterByGender } from './scoreboard-view';
import {
  RIFLE_TOURNAMENT_ID,
  RIFLE_TOURNAMENT_SLUG,
  buildLeaderboard,
  formatDecimalTenths,
  formatPointsTenths,
  isParseError,
  summarizeShooter,
  type LeaderboardRow,
  type ShooterGender,
  type StoredScore,
  type StoredShooter,
  type StoredTournament,
} from './shooting';

/** Round 2 exists only for the seeded rifle tournament. */
export const RIFLE_ROUND2_TOURNAMENT_ID = RIFLE_TOURNAMENT_ID;

/** Each Round 2 shooter fires 5 shots and all of them count. */
export const ROUND2_SHOTS = 5;
export function round2ShotCount(tournament: { round2ShotsPerShooter?: number | null }): number {
  const value = tournament.round2ShotsPerShooter;
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 20 ? value : ROUND2_SHOTS;
}

/** How many men and women the move button starts with. */
export const ROUND2_DEFAULT_MALES = 20;
export const ROUND2_DEFAULT_FEMALES = 10;

export const ROUND2_DEFAULT_TEAM_NAMES = ['Team 1', 'Team 2', 'Team 3', 'Team 4', 'Team 5'] as const;

const TEAM_NAME_MAX = 40;

export function tournamentHasRound2(tournament: { id?: string | null; slug?: string | null } | null | undefined): boolean {
  if (!tournament) return false;
  return tournament.id === RIFLE_ROUND2_TOURNAMENT_ID || tournament.slug === RIFLE_TOURNAMENT_SLUG;
}

export interface GenderCounts {
  male: number;
  female: number;
}

/** "Move top 20 men + top 10 women to Round 2", following the counts the admin chose. */
export function moveButtonLabel(counts: GenderCounts): string {
  const men = counts.male === 1 ? 'top 1 man' : `top ${counts.male} men`;
  const women = counts.female === 1 ? 'top 1 woman' : `top ${counts.female} women`;
  if (counts.male > 0 && counts.female > 0) return `Move ${men} + ${women} to Round 2`;
  if (counts.male > 0) return `Move ${men} to Round 2`;
  if (counts.female > 0) return `Move ${women} to Round 2`;
  return 'Move to Round 2';
}

export interface Round2Candidate {
  shooterId: string;
  name: string;
  bib: string | null;
  gender: ShooterGender | null;
  /** Overall Round 1 rank. 0 means no score yet. */
  rank: number;
  totalDisplay: string;
}

export interface Round2Pick extends Round2Candidate {
  /** Rank inside this shooter's gender, after the within-gender re-rank. */
  genderRank: number;
  dob: string | null;
}

export interface Round2Selection {
  male: Round2Pick[];
  female: Round2Pick[];
  /** Round 1 shooters with no Men / Women tag. They are not ranked and are not moved. */
  missingGender: Round2Candidate[];
}

export interface DobLookup {
  (row: { bib: string | null }): { dob: string | null; missing: boolean };
}

/** Whole number from 0 up to how many shooters of that gender are on the Round 1 roster. */
export function parseGenderCount(
  value: unknown,
  label: 'men' | 'women',
  available: number,
): { ok: true; count: number } | { ok: false; error: string } {
  const count =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && /^\d+$/.test(value.trim())
        ? Number(value.trim())
        : Number.NaN;
  if (!Number.isInteger(count) || count < 0) return { ok: false, error: `Choose how many ${label} to move.` };
  if (count > available) {
    if (available === 0) return { ok: false, error: `No ${label} are tagged yet. Set Men / Women on the shooter page first.` };
    return { ok: false, error: `There are ${available} ${label} in Round 1. Choose 0 to ${available}.` };
  }
  return { ok: true, count };
}

export function parseGenderCounts(
  input: { male?: unknown; female?: unknown },
  available: GenderCounts,
): { ok: true; counts: GenderCounts } | { ok: false; error: string } {
  const male = parseGenderCount(input.male, 'men', available.male);
  if (isParseError(male)) return male;
  const female = parseGenderCount(input.female, 'women', available.female);
  if (isParseError(female)) return female;
  if (male.count === 0 && female.count === 0) {
    return { ok: false, error: 'Choose at least one man or woman to move.' };
  }
  return { ok: true, counts: { male: male.count, female: female.count } };
}

/**
 * Top `count` rows of one already-ranked list.
 * A tie that falls on the last place is broken by the younger shooter (later date of birth,
 * same rule as age groups), then the lower bib. Missing dates lose to a real date.
 * Groups that fit entirely keep the leaderboard order.
 */
export function selectTopN<T extends { shooterId: string; name: string; bib: string | null; rank: number }>(
  rows: readonly T[],
  count: number,
  dobOf: DobLookup,
): T[] {
  if (count <= 0) return [];
  const selected: T[] = [];
  for (const group of tieGroups(rows)) {
    const room = count - selected.length;
    if (room <= 0) break;
    if (group.length <= room) {
      selected.push(...group);
      continue;
    }
    const ordered = [...group].sort((a, b) => compareYoungerThenBib(a, b, dobOf));
    selected.push(...ordered.slice(0, room));
    break;
  }
  return selected;
}

/**
 * Rank men among men and women among women, then take the top of each list.
 * A woman qualifies on her rank among women, even when men scored higher overall.
 * Shooters with no gender are listed and left out of both lists.
 */
export function selectRound2Qualifiers(
  rows: readonly Round2Candidate[],
  counts: GenderCounts,
  dobOf: DobLookup,
): Round2Selection {
  return {
    male: picksFor(rows, 'male', counts.male, dobOf),
    female: picksFor(rows, 'female', counts.female, dobOf),
    missingGender: rows.filter((row) => row.gender == null),
  };
}

export function dobLookupFromRegistrations(registrations: readonly AgeGroupRegistrationInput[]): DobLookup {
  return (row) => dobForBib(row.bib, registrations);
}

function picksFor(
  rows: readonly Round2Candidate[],
  gender: ShooterGender,
  count: number,
  dobOf: DobLookup,
): Round2Pick[] {
  const ranked = filterByGender([...rows], gender);
  return selectTopN(ranked, count, dobOf).map((row) => {
    const dob = dobOf(row);
    return { ...row, genderRank: row.rank, dob: dob.missing ? null : dob.dob };
  });
}

function tieGroups<T extends { rank: number }>(rows: readonly T[]): T[][] {
  const groups: T[][] = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    if (last && last[0]!.rank === row.rank) last.push(row);
    else groups.push([row]);
  }
  return groups;
}

/** Younger (later date of birth) first. A missing date sorts last. Then the lower bib. */
function compareYoungerThenBib<T extends { shooterId: string; name: string; bib: string | null }>(
  a: T,
  b: T,
  dobOf: DobLookup,
): number {
  const aDob = dobOf(a);
  const bDob = dobOf(b);
  if (aDob.missing !== bDob.missing) return aDob.missing ? 1 : -1;
  if (!aDob.missing && !bDob.missing && aDob.dob !== bDob.dob) return aDob.dob! < bDob.dob! ? 1 : -1;
  return compareBib(a.bib, b.bib) || a.name.localeCompare(b.name) || a.shooterId.localeCompare(b.shooterId);
}

export function parseTeamName(value: unknown): { ok: true; name: string } | { ok: false; error: string } {
  if (typeof value !== 'string') return { ok: false, error: 'Enter a team name.' };
  const name = value.trim().replace(/\s+/g, ' ');
  if (!name) return { ok: false, error: 'Enter a team name.' };
  if (name.length > TEAM_NAME_MAX) return { ok: false, error: `Keep the team name to ${TEAM_NAME_MAX} characters.` };
  return { ok: true, name };
}

/** Next default name: Team 6 after Team 1–Team 5, even if one of those was renamed. */
export function nextTeamName(names: readonly string[]): string {
  let max = 0;
  for (const name of names) {
    const match = /^Team (\d+)$/.exec(name.trim());
    if (match) max = Math.max(max, Number(match[1]));
  }
  let n = Math.max(max + 1, names.length + 1);
  const taken = new Set(names.map((name) => name.trim().toLowerCase()));
  while (taken.has(`team ${n}`)) n += 1;
  return `Team ${n}`;
}

export interface Round2ShooterOrder {
  teamId: string | null;
  name: string;
  bib: string | null;
}

/** Unassigned shooters first, then each team in its saved order. */
export function orderRound2Roster<T extends Round2ShooterOrder>(
  shooters: readonly T[],
  teams: readonly { id: string; sortOrder: number }[],
): T[] {
  const teamOrder = new Map(teams.map((team) => [team.id, team.sortOrder]));
  return [...shooters].sort((a, b) => {
    const aAssigned = a.teamId ? 1 : 0;
    const bAssigned = b.teamId ? 1 : 0;
    if (aAssigned !== bAssigned) return aAssigned - bAssigned;
    const aTeam = a.teamId ? (teamOrder.get(a.teamId) ?? 999) : -1;
    const bTeam = b.teamId ? (teamOrder.get(b.teamId) ?? 999) : -1;
    if (aTeam !== bTeam) return aTeam - bTeam;
    return compareBib(a.bib, b.bib) || a.name.localeCompare(b.name);
  });
}

export function round2BalanceNotes(
  teams: readonly { name: string; memberCount: number; round1MemberCount?: number }[],
): string[] {
  const notes: string[] = [];
  for (const team of teams) {
    if (team.round1MemberCount == null || team.memberCount === team.round1MemberCount) continue;
    const people = team.memberCount === 1 ? '1 shooter' : `${team.memberCount} shooters`;
    notes.push(`${team.name} has ${people}; its Round 1 team has ${team.round1MemberCount}.`);
  }
  return notes;
}

export interface Round2ShotInput {
  shotNumber: number;
  pointsTenths: number;
}

/** Round 2 total for one shooter: the sum of the shots saved so far (all 5 count). */
export function round2ShooterTotal(shots: readonly Round2ShotInput[], shotCount = ROUND2_SHOTS): {
  totalTenths: number;
  shotsFired: number;
  totalDisplay: string;
} {
  const summary = summarizeShooter(
    shots.map((shot) => ({
      id: '',
      tournamentId: '',
      shooterId: '',
      seriesNumber: 1,
      shotNumber: shot.shotNumber,
      pointsTenths: shot.pointsTenths,
      innerTens: 0,
      createdAt: '',
      updatedAt: '',
    })),
    { shotsPerShooter: shotCount, countedShots: shotCount },
  );
  return {
    totalTenths: summary.totalTenths,
    shotsFired: summary.shotsFired,
    totalDisplay: formatMixedTenths(summary.totalTenths),
  };
}

export interface Round2TeamStandingMember {
  shooterId: string;
  name: string;
  bib: string | null;
  gender: ShooterGender | null;
  /** This member's Round 2 total. */
  totalDisplay: string;
  shotsFired: number;
}

export interface Round2TeamStanding {
  rank: number;
  teamId: string;
  name: string;
  /** Round 2 team total. This is what the board ranks on. */
  totalTenths: number;
  totalDisplay: string;
  /** Round 1 team total, shown next to the Round 2 score. */
  round1TotalDisplay: string;
  /** Round 1 + Round 2. Information only; it does not change the rank. */
  overallDisplay: string;
  memberCount: number;
  members: Round2TeamStandingMember[];
}

export interface Round2TeamMemberInput {
  shooterId: string;
  teamId: string | null;
  name: string;
  bib: string | null;
  gender: ShooterGender | null;
  totalTenths: number;
  /** Round 1 total for this shooter. Omitted totals count as 0. */
  round1Tenths?: number;
  shotsFired: number;
}

/**
 * Team score is the sum of its members' Round 2 totals, highest first.
 * Equal totals share a rank. Unassigned shooters are not a team.
 */
export function buildTeamStandings(
  teams: readonly { id: string; name: string; sortOrder: number }[],
  members: readonly Round2TeamMemberInput[],
): Round2TeamStanding[] {
  const built = teams
    .map((team) => {
      const people = members
        .filter((member) => member.teamId === team.id)
        .sort((a, b) => b.totalTenths - a.totalTenths || compareBib(a.bib, b.bib) || a.name.localeCompare(b.name));
      const totalTenths = people.reduce((sum, person) => sum + person.totalTenths, 0);
      const round1Tenths = people.reduce((sum, person) => sum + (person.round1Tenths ?? 0), 0);
      return { team, people, totalTenths, round1Tenths };
    })
    .sort(
      (a, b) =>
        b.totalTenths - a.totalTenths || a.team.sortOrder - b.team.sortOrder || a.team.name.localeCompare(b.team.name),
    );

  let lastRank = 0;
  let previous: number | null = null;
  const hasShots = members.some((member) => member.teamId && member.shotsFired > 0);
  return built.map((row, index) => {
    const rank = previous != null && previous === row.totalTenths ? lastRank : index + 1;
    lastRank = rank;
    previous = row.totalTenths;
    return {
      rank: hasShots ? rank : 0,
      teamId: row.team.id,
      name: row.team.name,
      totalTenths: row.totalTenths,
      totalDisplay: hasShots ? formatMixedTenths(row.totalTenths) : '—',
      round1TotalDisplay: formatMixedTenths(row.round1Tenths),
      overallDisplay: formatMixedTenths(row.totalTenths + row.round1Tenths),
      memberCount: row.people.length,
      members: row.people.map((person) => ({
        shooterId: person.shooterId,
        name: person.name,
        bib: person.bib,
        gender: person.gender,
        totalDisplay: formatMixedTenths(person.totalTenths),
        shotsFired: person.shotsFired,
      })),
    };
  });
}

function formatMixedTenths(tenths: number): string {
  return tenths % 10 === 0 ? formatPointsTenths(tenths) : formatDecimalTenths(tenths);
}

export interface PublicRound2 {
  shotsPlanned: number;
  shotsCounted: number;
  maxDisplay: string;
  qualifyingTeams: number;
  teams: Round2TeamStanding[];
}

export interface Round2BoardShooter {
  id: string;
  name: string;
  bib: string | null;
  gender: ShooterGender | null;
  sortOrder: number;
  createdAt: string;
}

export interface Round2BoardScore {
  id: string;
  round2ShooterId: string;
  shotNumber: number;
  pointsTenths: number;
  createdAt: string;
  updatedAt: string;
}

export interface Round2AdminShooter {
  id: string;
  sourceShooterId: string;
  name: string;
  bib: string | null;
  gender: ShooterGender | null;
  teamId: string | null;
  totalDisplay: string;
  shotsFired: number;
  /** Round 2 individual rank. 0 until they have a shot. */
  rank: number;
}

export interface Round2AdminTeam {
  id: string;
  name: string;
  sortOrder: number;
  memberCount: number;
}

export interface Round2Person {
  shooterId: string;
  name: string;
  bib: string | null;
  gender: ShooterGender | null;
}

export interface Round2ScoreView {
  id: string;
  round2ShooterId: string;
  shotNumber: number;
  pointsDisplay: string;
}

/** Admin Round 2 screen. Not returned by the public API. */
export interface Round2AdminView {
  shotsPerShooter: number;
  setup: boolean;
  publicVisible: boolean;
  maleAvailable: number;
  femaleAvailable: number;
  existingCount: number;
  warnings: string[];
  teams: Round2AdminTeam[];
  shooters: Round2AdminShooter[];
  available: Round2Person[];
  missingGender: Round2Person[];
  /** Round 2 is ranked by team. Individual rows are the roster, not a second board. */
  teamBoard: Round2TeamStanding[];
  promotion: {
    qualifyingTeams: number;
    qualified: { teamId: string; name: string; totalDisplay: string }[];
    tied: { teamId: string; name: string; totalDisplay: string; tens: number }[];
    slots: number;
  };
  scores: Round2ScoreView[];
}

export interface Round2Preview {
  counts: GenderCounts;
  men: Round2Pick[];
  women: Round2Pick[];
  missingGender: Round2Person[];
  existingCount: number;
  /** Qualifiers who are not already on the Round 2 roster. */
  willAdd: number;
}

/** Individual Round 2 board. Uses the same ranking as Round 1, over the 5 Round 2 shots only. */
export function buildRound2Individual(
  tournamentId: string,
  shooters: readonly Round2BoardShooter[],
  scores: readonly Round2BoardScore[],
  shotCount = ROUND2_SHOTS,
): LeaderboardRow[] {
  const tournament: StoredTournament = {
    id: tournamentId,
    slug: 'round-2',
    title: 'Round 2',
    status: 'live',
    shotsPerShooter: shotCount,
    countedShots: shotCount,
    published: false,
    displayPage: 0,
    createdAt: '',
    updatedAt: '',
  };
  const storedShooters: StoredShooter[] = shooters.map((shooter) => ({
    id: shooter.id,
    tournamentId,
    name: shooter.name,
    bib: shooter.bib,
    sortOrder: shooter.sortOrder,
    createdAt: shooter.createdAt,
    gender: shooter.gender,
  }));
  const storedScores: StoredScore[] = scores.map((score) => ({
    id: score.id,
    tournamentId,
    shooterId: score.round2ShooterId,
    seriesNumber: 1,
    shotNumber: score.shotNumber,
    pointsTenths: score.pointsTenths,
    innerTens: 0,
    createdAt: score.createdAt,
    updatedAt: score.updatedAt,
  }));
  return buildLeaderboard(tournament, storedShooters, storedScores).rows;
}
